import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';
import { Mail, Lock, User } from 'lucide-react';

export default function Auth() {
  const { t, lang, signIn, signUp } = useApp();
  const [params] = useSearchParams();
  const initial = params.get('mode') === 'signup' ? 'signup' : 'signin';
  const [mode, setMode] = useState(initial);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true); setError('');
    try {
      if (mode === 'signin') await signIn(email, password);
      else await signUp(name || email.split('@')[0], email, password);
      nav('/');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Error';
      setError(typeof msg === 'string' ? msg : 'Error');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#0a0a0b]">
      <div className="w-full max-w-md bg-[#101012] border border-white/10 rounded-2xl p-7 fade-up">
        <div className="flex justify-center mb-5"><Logo size={30} /></div>
        <h1 className="text-center font-display text-2xl font-extrabold">
          {t('auth.welcome')} <span className="logo-gradient">Flowly</span>
        </h1>
        <p className="text-center text-zinc-400 text-sm mt-1">{t('auth.tag')}</p>

        <div className="mt-6 grid grid-cols-2 bg-[#16161a] border border-white/10 rounded-lg p-1 text-sm">
          <button onClick={()=>setMode('signin')} className={`py-1.5 rounded-md ${mode==='signin'?'bg-white text-black':'text-zinc-300'}`}>{t('auth.signin_cta')}</button>
          <button onClick={()=>setMode('signup')} className={`py-1.5 rounded-md ${mode==='signup'?'bg-white text-black':'text-zinc-300'}`}>{t('auth.signup_cta')}</button>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode==='signup' && (
            <div>
              <Label className="text-zinc-300">{t('auth.name')}</Label>
              <div className="relative mt-1">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Jane Doe" className="pl-9 bg-[#16161a] border-white/10" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-zinc-300">{t('auth.email')}</Label>
            <div className="relative mt-1">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@studio.app" className="pl-9 bg-[#16161a] border-white/10" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">{t('auth.password')}</Label>
            <div className="relative mt-1">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="••••••••" className="pl-9 bg-[#16161a] border-white/10" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-zinc-200 h-10">
            {loading ? '...' : (mode==='signin' ? t('auth.signin_cta') : t('auth.signup_cta'))}
          </Button>
          {error && <div className="text-xs text-red-400 text-center">{error}</div>}
        </form>

        <div className="flex items-center gap-3 my-5 text-xs text-zinc-500">
          <div className="h-px bg-white/10 flex-1" />
          {t('auth.or')}
          <div className="h-px bg-white/10 flex-1" />
        </div>
        <Button variant="outline" className="w-full border-white/15 bg-transparent hover:bg-white/5">{t('auth.continue_google')}</Button>

        <p className="mt-5 text-center text-xs text-zinc-500">
          {mode==='signin' ? t('auth.no_account') : t('auth.have_account')}{' '}
          <button onClick={()=>setMode(mode==='signin'?'signup':'signin')} className="text-white underline-offset-2 hover:underline">
            {mode==='signin' ? t('auth.signup_cta') : t('auth.signin_cta')}
          </button>
        </p>
        <p className="mt-3 text-center text-[11px] text-zinc-600">
          <Link to="/" className="hover:text-zinc-400">{lang==='fr'?'Retour':'Back'}</Link>
        </p>
      </div>
    </div>
  );
}
